import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, Data } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PageSeoInfo } from 'src/classes/page-seo-info';
import { TranslatorService } from './translator.service';

const SITE_URL = 'https://www.evok-performance.com';
const SITE_NAME = 'EVOK Performance';
const TWITTER_HANDLE = '@evokperformance';
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/images/og-default.jpg`;
const SUPPORTED_LANGS = ['it', 'en', 'es', 'fr'];
const DEFAULT_LANG = 'en';
const OG_LOCALE_MAP: Record<string, string> = {
  it: 'it_IT',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR'
};

const STATIC_PAGE_PATHS: Record<string, string> = {
  home: '/home',
  aboutUs: '/about-us',
  storeLocator: '/store-locator',
  downloadCatalogo: '/download/catalog',
  downloadIstruzioni: '/download/instructions',
  privacyPolicy: '/privacy-policy',
  searchProducts: '/search-products',
  productApplication: '/search-application'
};

const DYNAMIC_NOINDEX_PAGES = new Set(['searchFullText']);

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    protected titleService: Title,
    protected metaService: Meta,
    private router: Router,
    private translatorService: TranslatorService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  setTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  updateTag(name: string, content: string): void {
    this.metaService.updateTag({ name, content });
  }

  setSeoData(): void {
    this.router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe(() => {
        let root = this.router.routerState.snapshot.root;
        while (root) {
          if (root.children && root.children.length) {
            root = root.children[0];
          } else {
            this.applySeo(root.data);
            return;
          }
        }
      });
  }

  private applySeo(data: Data): void {
    if (!data || !data['seoNode']) return;

    const pageinfos = this.translatorService.getLocalizationData<Array<PageSeoInfo>>(data['seoNode']);
    if (!pageinfos) return;

    const pageInfoKey: string = data['pageInfo'];
    const info = pageinfos.find(i => i.component === pageInfoKey);
    if (!info) return;

    const lang = this.detectLanguage();
    const canonicalPath = this.resolveCanonicalPath(pageInfoKey);
    const canonicalUrl = `${SITE_URL}/${lang}${canonicalPath}`;
    const description = info.metatags?.description || '';
    const ogImage = info.ogImage || DEFAULT_OG_IMAGE;
    const ogType = info.ogType || 'website';
    const noindex = info.noindex || DYNAMIC_NOINDEX_PAGES.has(pageInfoKey);

    this.titleService.setTitle(info.title);
    this.setMetaName('description', description);
    this.setMetaName('keywords', info.metatags?.keywords);

    this.setRobots(noindex);
    this.setHtmlLang(lang);
    this.setCanonical(canonicalUrl);
    this.setHreflangAlternates(canonicalPath);

    this.setMetaProperty('og:title', info.title);
    this.setMetaProperty('og:description', description);
    this.setMetaProperty('og:url', canonicalUrl);
    this.setMetaProperty('og:type', ogType);
    this.setMetaProperty('og:site_name', SITE_NAME);
    this.setMetaProperty('og:image', ogImage);
    this.setMetaProperty('og:image:alt', info.title);
    this.setMetaProperty('og:locale', OG_LOCALE_MAP[lang] || OG_LOCALE_MAP[DEFAULT_LANG]);
    this.setOgLocaleAlternates(lang);

    this.setMetaName('twitter:card', 'summary_large_image');
    this.setMetaName('twitter:title', info.title);
    this.setMetaName('twitter:description', description);
    this.setMetaName('twitter:image', ogImage);
    this.setMetaName('twitter:site', TWITTER_HANDLE);

    if (info.metatags) {
      for (const key of Object.keys(info.metatags)) {
        if (key === 'description' || key === 'keywords') continue;
        const value = info.metatags[key];
        if (value) this.metaService.updateTag({ name: key, content: value });
      }
    }
  }

  private detectLanguage(): string {
    const path = this.document.location?.pathname || '/';
    const seg = path.split('/')[1];
    return SUPPORTED_LANGS.includes(seg) ? seg : DEFAULT_LANG;
  }

  private resolveCanonicalPath(pageInfoKey: string): string {
    if (STATIC_PAGE_PATHS[pageInfoKey]) return STATIC_PAGE_PATHS[pageInfoKey];
    const pathname = this.document.location?.pathname || '/';
    const seg = pathname.split('/');
    if (seg.length > 1 && SUPPORTED_LANGS.includes(seg[1])) {
      const stripped = '/' + seg.slice(2).join('/');
      return stripped === '/' ? '/home' : stripped;
    }
    return pathname === '/' ? '/home' : pathname;
  }

  private setMetaName(name: string, content: string | undefined): void {
    if (content && content.length) {
      this.metaService.updateTag({ name, content });
    } else {
      this.metaService.removeTag(`name="${name}"`);
    }
  }

  private setMetaProperty(property: string, content: string | undefined): void {
    if (content && content.length) {
      this.metaService.updateTag({ property, content }, `property="${property}"`);
    } else {
      this.metaService.removeTag(`property="${property}"`);
    }
  }

  private setRobots(noindex?: boolean): void {
    const value = noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
    this.setMetaName('robots', value);
  }

  private setHtmlLang(lang: string): void {
    const html = this.document.documentElement;
    if (html) html.setAttribute('lang', lang);
  }

  private setCanonical(url: string): void {
    const head = this.document.head;
    if (!head) return;
    let link = head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setHreflangAlternates(canonicalPath: string): void {
    const head = this.document.head;
    if (!head) return;

    head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => {
      el.parentNode?.removeChild(el);
    });

    SUPPORTED_LANGS.forEach(l => {
      const link = this.document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', l);
      link.setAttribute('href', `${SITE_URL}/${l}${canonicalPath}`);
      head.appendChild(link);
    });

    const xDefault = this.document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', `${SITE_URL}/${DEFAULT_LANG}${canonicalPath}`);
    head.appendChild(xDefault);
  }

  private setOgLocaleAlternates(currentLang: string): void {
    this.metaService.removeTag('property="og:locale:alternate"');
    Object.keys(OG_LOCALE_MAP)
      .filter(l => l !== currentLang)
      .forEach(l => {
        this.metaService.addTag({ property: 'og:locale:alternate', content: OG_LOCALE_MAP[l] } as any);
      });
  }
}
